/**
 * @file ContactNotesModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-07
 * @modified 2026-09-07
 * @description The customer notes field, on its own.
 *
 *              A rep is view-only everywhere except this. Rather than open the
 *              full contact editor and hide everything they may not touch —
 *              which is a promise that has to be re-kept every time a field is
 *              added to that form — they get a dialog that only has the one
 *              field, talking to an endpoint that can only write the one
 *              column. Neither can drift into letting them change an address.
 *
 *              Offered to everyone else too, from the same button, when they
 *              only want to leave a note: it is the fast path, and the full
 *              editor is still there for anyone who can open it.
 */

import React, { useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Textarea from './Textarea';
import { useSlideOutButton } from './SlideOutButton';
import './ContactNotesModal.css';

/**
 * Its own component so useSlideOutButton fires in the same render cycle as the
 * buttons it measures — from the parent it would run before Modal.js has put
 * the portal content in the DOM. See CLAUDE.md.
 */
function NotesBody({ contact, notes, setNotes, error, saving }) {
  useSlideOutButton([saving]);
  return (
    <div className="contact-notes-body">
      {error && <div className="formulation-error">{error}</div>}
      <p className="contact-notes-who">
        {contact?.account_no && <span className="account-badge-inline">{contact.account_no}</span>}
        {contact?.company_name}
      </p>
      <label htmlFor="contact-notes-field">Notes</label>
      <Textarea
        id="contact-notes-field"
        rows={10}
        value={notes}
        placeholder="Anything worth knowing about this customer…"
        onChange={(e) => setNotes(e.target.value)}
      />
      <span className="contact-notes-hint">
        Visible to everyone who opens this customer.
      </span>
    </div>
  );
}

export default function ContactNotesModal({ contact, onClose, onSaved }) {
  const [notes, setNotes] = useState(contact?.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/contacts/${contact.id}/notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not save the notes');
      onSaved?.(data.contact?.notes ?? notes);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Edit customer notes"
      size="md"
      className="contact-notes"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            {/* data-read-safe because a modal footer defaults to DENY for a
                view-only account (see App.css), and this is the one write such
                an account IS allowed — the server carve-out and this have to
                agree or the button is dead on a request that would succeed. */}
            <button data-read-safe className="btn-slideout" onClick={save} disabled={saving}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${saving ? ' spinning' : ''}`}>
                  {saving ? 'progress_activity' : 'save'}
                </span>
              </div>
              <span className="btn-label">{saving ? 'Saving…' : 'Save notes'}</span>
            </button>
          </div>
        </div>
      }
    >
      <NotesBody
        contact={contact}
        notes={notes}
        setNotes={setNotes}
        error={error}
        saving={saving}
      />
    </Modal>
  );
}
